import { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  type ChartOptions,
} from "chart.js";
import { DashboardService } from "../../services";
import type { ISearchTermsGrowth } from "../../types";
import {
  gruposDoGrafico,
  mesAindaNaoLiberado,
  rotuloDoGrupo,
  rotuloDoMes,
  seloDeCrescimento,
  temBuscas,
} from "./search-terms";
import {
  Aviso,
  Bloco,
  Cabecalho,
  Grafico,
  Linha,
  ListaDeTermos,
  Nota,
  SemSelo,
  Selo,
} from "./styles";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const MESES_NO_GRAFICO = 12;
const TERMOS_NA_LISTA = 10;
const CORES = ["#1a73e8", "#34a853", "#f9ab00", "#ea4335", "#9334e6"];

const opcoes: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { position: "bottom", labels: { boxWidth: 10, usePointStyle: true } },
    tooltip: {
      callbacks: {
        label: (contexto) => `${contexto.dataset.label}: ${contexto.parsed.y ?? 0} buscas`,
      },
    },
  },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0 } },
    x: { grid: { display: false } },
  },
};

type Props = {
  profileId: string;
};

/**
 * "Como as buscas por você evoluíram": termos que as pessoas digitaram no
 * Google e mostraram a empresa, mês a mês, com o total e os maiores grupos.
 * Carrega à parte para não atrasar o resto do dashboard.
 */
export const SearchTermsGrowth = ({ profileId }: Props) => {
  const [dados, setDados] = useState<ISearchTermsGrowth | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    DashboardService.getSearchTermsGrowth(profileId, MESES_NO_GRAFICO)
      .then((resposta) => {
        if (ativo) setDados(resposta ?? null);
      })
      .catch(() => {
        if (ativo) setDados(null);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [profileId]);

  const grafico = useMemo(() => {
    if (!temBuscas(dados)) return null;
    return {
      labels: dados.months.map(rotuloDoMes),
      datasets: [
        {
          label: "Todas as buscas",
          data: dados.totalByMonth,
          borderColor: CORES[0],
          backgroundColor: "rgba(26, 115, 232, 0.08)",
          fill: true,
          tension: 0.35,
          borderWidth: 3,
        },
        ...gruposDoGrafico(dados.groups, 4).map((grupo, indice) => ({
          label: rotuloDoGrupo(grupo),
          data: grupo.values,
          borderColor: CORES[(indice + 1) % CORES.length],
          backgroundColor: "transparent",
          tension: 0.35,
          borderWidth: 2,
        })),
      ],
    };
  }, [dados]);

  const termos = useMemo(
    () => (dados?.terms ?? []).filter((termo) => termo.total > 0).slice(0, TERMOS_NA_LISTA),
    [dados],
  );

  const maisTermosPequenos = dados ? Math.max(0, ...dados.belowThresholdByMonth) : 0;

  return (
    <Bloco aria-labelledby="titulo-buscas-google">
      <Cabecalho>
        <h3 id="titulo-buscas-google">Como as buscas por você evoluíram</h3>
        <p>
          O que as pessoas digitaram no Google e fez sua empresa aparecer, mês a
          mês. Dados do próprio Google.
        </p>
      </Cabecalho>

      {carregando ? (
        <Aviso>Carregando as buscas do Google…</Aviso>
      ) : !temBuscas(dados) || !grafico ? (
        <Aviso>
          O Google ainda não tem buscas suficientes para mostrar a evolução desta
          empresa.
        </Aviso>
      ) : (
        <>
          <Grafico>
            <Line data={grafico} options={opcoes} />
          </Grafico>
          {mesAindaNaoLiberado(dados.months) && (
            <Nota>
              Os dados de {mesAindaNaoLiberado(dados.months)} ainda não foram
              liberados pelo Google — o gráfico vai até o último mês disponível.
            </Nota>
          )}

          <ListaDeTermos aria-label="Termos mais buscados">
            {termos.map((termo) => {
              const selo = seloDeCrescimento(termo.growthPercent, termo.trend);
              return (
                <Linha key={termo.term}>
                  <span>
                    <span className="termo">{termo.term}</span>
                    {termo.kind !== "TOPIC" && (
                      <span className="grupo">{rotuloDoGrupo({ kind: termo.kind, name: termo.group })}</span>
                    )}
                  </span>
                  <strong>{termo.total.toLocaleString("pt-BR")}</strong>
                  {selo ? <Selo $novo={termo.trend === "NEW"}>{selo}</Selo> : <SemSelo />}
                </Linha>
              );
            })}
          </ListaDeTermos>

          {maisTermosPequenos > 0 && (
            <Nota>
              Além destes, o Google registrou até {maisTermosPequenos} buscas
              diferentes por mês que apareceram menos de {dados.threshold ?? 15} vezes
              cada — ele não informa o número exato dessas.
            </Nota>
          )}
        </>
      )}
    </Bloco>
  );
};
